import React, { useEffect, useRef, useState } from "react"
import { Chart, registerables } from "chart.js"
import zoomPlugin from "chartjs-plugin-zoom"
import { Analytics } from "../../Apis/analyticsApi"
import { Spin } from "antd"
import { translations } from "../../utils/Translation/staticTranslation"

// Register chart.js components and plugins
Chart.register(...registerables, zoomPlugin)

const BusinessMetricsChart = ({ Title = "Business Metrics" }) => {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)
  const [loading, setLoading] = useState(false)
  const [businessData, setBusinessData] = useState({ metrics: [] })
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const years = [2022, 2023, 2024, 2025]
  const monthAbbreviations = {
    January: "Jan",
    February: "Feb",
    March: "Mar",
    April: "Apr",
    May: "May",
    June: "Jun",
    July: "Jul",
    August: "Aug",
    September: "Sep",
    October: "Oct",
    November: "Nov",
    December: "Dec",
  }

  const lang = localStorage.getItem("language") || "ar"
  const dashboardAdmin = translations[lang]?.DashboardSuperAdmin

  // Prevent duplicate API calls
  const fetchInProgress = useRef(false)
  const prevYearRef = useRef(null)

  const fetchBusinessMetrics = async (year) => {
    // Prevent duplicate calls
    if (fetchInProgress.current || prevYearRef.current === year) {
      return
    }

    fetchInProgress.current = true
    prevYearRef.current = year
    setLoading(true)

    try {
      const response = await Analytics.getBusinessMetrics(year)
      if (response.status === 200) {
        setBusinessData(response.data)
      }
    } catch (error) {
      console.error("Error fetching business metrics:", error)
      setBusinessData({ metrics: [] })
      prevYearRef.current = null // Reset on error to allow retry
    } finally {
      setLoading(false)
      fetchInProgress.current = false
    }
  }

  useEffect(() => {
    if (selectedYear) {
      fetchBusinessMetrics(selectedYear)
    }
  }, [selectedYear])

  const renderChart = () => {
    if (!chartRef.current || !businessData.metrics) return

    // For a stacked bar chart, we need to calculate the base business (total minus new)
    const newBusinessData = businessData.metrics.map(
      (item) => item.new_businesses
    )

    // Calculate base business (total minus new)
    const baseBusinessData = businessData.metrics.map(
      (item) => item.total_businesses - item.new_businesses
    )

    const labels = businessData.metrics.map(
      (item) => monthAbbreviations[item.month] || item.month
    )

    // Destroy previous chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy()
    }

    const ctx = chartRef.current.getContext("2d")

    // Create new stacked bar chart
    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: labels,
        datasets: [
          {
            label: dashboardAdmin?.existing_business,
            data: baseBusinessData,
            backgroundColor: "rgba(54, 162, 235, 0.7)", // Blue for base business
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 1,
            borderRadius: {
              topLeft: 0,
              topRight: 0,
              bottomLeft: 4,
              bottomRight: 4,
            },
          },
          {
            label: dashboardAdmin?.new_business,
            data: newBusinessData,
            backgroundColor: "rgba(255, 99, 132, 0.7)", // Red for new business
            borderColor: "rgba(255, 99, 132, 1)",
            borderWidth: 1,
            borderRadius: {
              topLeft: 4,
              topRight: 4,
              bottomLeft: 0,
              bottomRight: 0,
            },
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            stacked: true,
            grid: { display: true, drawBorder: true },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return value >= 1000 ? `${value / 1000}k` : value
              },
            },
          },
        },
        plugins: {
          legend: {
            display: true,
            position: "top",
          },
          tooltip: {
            mode: "index",
            intersect: false,
            callbacks: {
              label: function (context) {
                let label = context.dataset.label || ""
                if (label) label += ": "
                label += context.parsed.y.toLocaleString()
                return label
              },
              footer: function (tooltipItems) {
                let sum = 0
                tooltipItems.forEach(function (tooltipItem) {
                  sum += tooltipItem.parsed.y
                })
                return "Total: " + sum.toLocaleString()
              },
            },
          },
          zoom: {
            pan: { enabled: false, mode: "x" },
            zoom: {
              wheel: { enabled: false },
              pinch: { enabled: false },
              mode: "x",
            },
          },
        },
        layout: { padding: { top: 10 } },
      },
    })
  }

  useEffect(() => {
    if (businessData.metrics && businessData.metrics.length > 0) {
      renderChart()
    } else if (!loading && chartRef.current) {
      // Handle empty data case
      if (chartInstance.current) {
        chartInstance.current.destroy()
      }

      const ctx = chartRef.current.getContext("2d")
      chartInstance.current = new Chart(ctx, {
        type: "bar",
        data: {
          labels: Object.values(monthAbbreviations),
          datasets: [
            {
              label: "No data available",
              data: Array(12).fill(0),
              backgroundColor: "rgba(200, 200, 200, 0.3)",
              borderColor: "rgba(200, 200, 200, 0.5)",
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: "top",
            },
            tooltip: {
              enabled: false,
            },
          },
        },
      })
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy()
      }
    }
  }, [businessData, loading])

  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value))
  }

  return (
    <div className="h-100">
      {loading ? (
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: "15px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h4 style={{ margin: 0 }}>{Title}</h4>
              <div>
                <select
                  value={selectedYear}
                  onChange={handleYearChange}
                  style={selectStyle}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div style={{ position: "relative", width: "100%", height: "400px" }}>
            <canvas
              ref={chartRef}
              style={{ height: "400px", width: "100%" }}
            ></canvas>
          </div>
        </>
      )}
    </div>
  )
}

const selectStyle = {
  padding: "8px 12px",
  fontSize: "14px",
  borderRadius: "5px",
  border: "1px solid #ddd",
  backgroundColor: "white",
  cursor: "pointer",
}

export default BusinessMetricsChart
